import { TITLE_METADATA_KEY } from "@/domain/case/case-config";

/**
 * 新規モデルの雛形。
 *
 * UI からモデルを作るとき、空の BPMN だけを置くと「一覧に件名が出ない・検索できない」
 * 状態から始まってしまう（metadata の宣言と、それを埋めるフォームの両方が必要）。
 * そのため雛形は次を必ず備える。
 *
 * - `metadata_extraction_paths` に件名の予約キー（`case_title`）の宣言
 * - 件名を入力する起案フォーム（これが無いと metadata は永久に空）
 * - 起案タスク id は `Activity_Draft`（front の下書き判定がこの名前を見る）
 * - `BPMNEdge`（bpmn-js は自動レイアウトしないので、無いと矢印が出ない）
 */
export type StarterModelInput = {
  /** `<group>/<model>` 形式の id。 */
  readonly processModelId: string;
  readonly displayName: string;
  readonly description?: string;
};

export type StarterModelConfig = {
  readonly id: string;
  readonly display_name: string;
  readonly description: string;
  readonly primary_file_name: string;
  readonly primary_process_id: string;
  readonly metadata_extraction_paths: readonly {
    readonly key: string;
    readonly path: string;
  }[];
};

export type StarterModel = {
  readonly config: StarterModelConfig;
  readonly bpmnFileName: string;
  readonly bpmnXml: string;
  readonly formFileName: string;
  readonly formJson: string;
};

export function buildStarterModel(input: StarterModelInput): StarterModel {
  const modelName = input.processModelId.split("/").pop() ?? "process";
  const bpmnFileName = `${modelName}.bpmn`;
  const processId = `Process_${toPascalCase(modelName)}`;

  return {
    config: {
      id: input.processModelId,
      display_name: input.displayName,
      description: input.description ?? "",
      primary_file_name: bpmnFileName,
      primary_process_id: processId,
      // 件名は全モデル共通の予約キー（一覧・検索がこれだけを見る）
      metadata_extraction_paths: [
        { key: TITLE_METADATA_KEY, path: TITLE_METADATA_KEY },
      ],
    },
    bpmnFileName,
    bpmnXml: buildBpmn(processId, input.displayName),
    formFileName: "draft-schema.json",
    formJson: buildFormJson(input),
  };
}

function toPascalCase(value: string): string {
  return value
    .split(/[-_]/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join("");
}

function buildFormJson(input: StarterModelInput): string {
  return `${JSON.stringify(
    {
      title: `${input.displayName} 起案`,
      type: "object",
      required: [TITLE_METADATA_KEY],
      properties: {
        [TITLE_METADATA_KEY]: { type: "string", title: "件名" },
      },
    },
    null,
    2,
  )}\n`;
}

/** 起案タスク 1 つだけの最小フロー。続きは設計画面で編集する。 */
function buildBpmn(processId: string, displayName: string): string {
  return `<?xml version="1.0" encoding="UTF-8"?>
<bpmn:definitions xmlns:bpmn="http://www.omg.org/spec/BPMN/20100524/MODEL" xmlns:bpmndi="http://www.omg.org/spec/BPMN/20100524/DI" xmlns:dc="http://www.omg.org/spec/DD/20100524/DC" xmlns:di="http://www.omg.org/spec/DD/20100524/DI" xmlns:spiffworkflow="http://spiffworkflow.org/bpmn/schema/1.0/core" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" id="Definitions_${processId}" targetNamespace="http://bpmn.io/schema/bpmn">
  <bpmn:process id="${processId}" name="${displayName}" isExecutable="true">
    <bpmn:startEvent id="StartEvent_1" name="開始">
      <bpmn:outgoing>Flow_Start_Draft</bpmn:outgoing>
    </bpmn:startEvent>
    <bpmn:sequenceFlow id="Flow_Start_Draft" sourceRef="StartEvent_1" targetRef="Activity_Draft" />

    <bpmn:userTask id="Activity_Draft" name="起案">
      <bpmn:extensionElements>
        <spiffworkflow:instructionsForEndUser>件名を入力してください。</spiffworkflow:instructionsForEndUser>
        <spiffworkflow:properties>
          <spiffworkflow:property name="formJsonSchemaFilename" value="draft-schema.json" />
        </spiffworkflow:properties>
      </bpmn:extensionElements>
      <bpmn:incoming>Flow_Start_Draft</bpmn:incoming>
      <bpmn:outgoing>Flow_Draft_End</bpmn:outgoing>
    </bpmn:userTask>
    <bpmn:sequenceFlow id="Flow_Draft_End" sourceRef="Activity_Draft" targetRef="Event_Done" />

    <bpmn:endEvent id="Event_Done" name="完了">
      <bpmn:incoming>Flow_Draft_End</bpmn:incoming>
    </bpmn:endEvent>
  </bpmn:process>

  <bpmndi:BPMNDiagram id="BPMNDiagram_1">
    <bpmndi:BPMNPlane id="BPMNPlane_1" bpmnElement="${processId}">
      <bpmndi:BPMNShape id="Shape_Start" bpmnElement="StartEvent_1">
        <dc:Bounds x="160" y="200" width="36" height="36" />
      </bpmndi:BPMNShape>
      <bpmndi:BPMNShape id="Shape_Draft" bpmnElement="Activity_Draft">
        <dc:Bounds x="250" y="178" width="120" height="80" />
      </bpmndi:BPMNShape>
      <bpmndi:BPMNShape id="Shape_Done" bpmnElement="Event_Done">
        <dc:Bounds x="430" y="200" width="36" height="36" />
      </bpmndi:BPMNShape>
      <bpmndi:BPMNEdge id="Edge_Flow_Start_Draft" bpmnElement="Flow_Start_Draft">
        <di:waypoint x="196" y="218" />
        <di:waypoint x="250" y="218" />
      </bpmndi:BPMNEdge>
      <bpmndi:BPMNEdge id="Edge_Flow_Draft_End" bpmnElement="Flow_Draft_End">
        <di:waypoint x="370" y="218" />
        <di:waypoint x="430" y="218" />
      </bpmndi:BPMNEdge>
    </bpmndi:BPMNPlane>
  </bpmndi:BPMNDiagram>
</bpmn:definitions>
`;
}
